import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Panel from '../../components/Panel'
import Plate from '../../components/Plate'
import Tag from '../../components/Tag'
import LoadingIndicator from "../../../../components/LoadingIndicator";
import useLotSearch from "../useLotSearch";

const useStyles = makeStyles({
  panel: { padding: 20 },
  header: { marginBottom: 12, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 },
  title: { margin: 0, fontSize: 20, fontWeight: 600, color: T.fg },
  uppercase: { textTransform: "uppercase" },
  results: { marginTop: 12 },
  empty: { borderRadius: 6, border: `1px dashed ${T.border}`, background: T.surface, padding: "16px 12px", textAlign: "center", fontSize: 12, color: T.mutedFg },
  loading: { display: "flex", justifyContent: "center", borderRadius: 6, border: `1px dashed ${T.border}`, background: T.surface, padding: "12px" },
  list: { margin: 0, padding: 0, listStyle: "none", overflow: "hidden", borderRadius: 6, border: `1px solid ${T.border}` },
  row: { display: "flex", alignItems: "center", gap: 12, background: T.surface, padding: "8px 12px" },
  rowBtn: { minWidth: 0, flex: 1, textAlign: "left", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit", padding: 0 },
  rowName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, color: T.fg },
  rowSub: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: T.mutedFg },
  statusTag: { whiteSpace: "nowrap" },
  more: { margin: "8px 0 0", fontSize: 11, color: T.mutedFg, textAlign: "center" },
});

const subLine = (car) =>
  [`Bay ${car.bay ?? "-"}`, car.parkedByName, car.vehicle]
    .filter(Boolean)
    .join(" · ");

export default function PlateSearch({ facilityId, onOpenCar }) {
  const s = useStyles();
  const ui = useUi();
  const [q, setQ] = useState("");

  const { items, count, loading, error, query, active } = useLotSearch(
    facilityId,
    q,
  );

  return (
    <Panel className={s.panel}>
      <div className={s.header}>
        <h2 className={s.title}>Plate lookup</h2>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Enter plate or guest name…"
        className={cx(ui.field, s.uppercase)}
      />
      {active && (
        <div className={s.results}>
          {loading ? (
            <div className={s.loading} aria-busy="true">
              <LoadingIndicator />
            </div>
          ) : error ? (
            /* The lookup is a live read, so a failure is worth retrying rather
             * than reading as "the car is not here". */
            <div className={s.empty}>Could not search the lot. Try again.</div>
          ) : items.length === 0 ? (
            <div className={s.empty}>No parked vehicle matches “{query}”.</div>
          ) : (
            <>
              <ul className={s.list}>
                {items.map((car, idx) => (
                  <li key={car.id} className={s.row} style={{ borderTop: idx === 0 ? "none" : `1px solid ${T.borderSoft}` }}>
                    <Plate size={12}>{car.plate}</Plate>
                    <button onClick={() => onOpenCar(car)} className={s.rowBtn} title="View vehicle details">
                      {/* An unmatched arrival has no booking behind it, so no name. */}
                      <div className={s.rowName}>{car.guestName || "Unmatched plate"}</div>
                      <div className={s.rowSub}>{subLine(car)}</div>
                    </button>
                    {car.status === "REQUESTED" ? (
                      <Tag tone="warn" size={10} upper className={s.statusTag}>Requested</Tag>
                    ) : (
                      <Tag tone="muted" size={10} upper className={s.statusTag}>Parked</Tag>
                    )}
                  </li>
                ))}
              </ul>
              {count > items.length && (
                <p className={s.more}>
                  Showing {items.length} of {count} matches - type more of the plate.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </Panel>
  );
}
